import {
  cancelPlanSubscriptionAutoRenewal,
  getPlanSubscription,
  getPlanSubscriptions,
} from '../../../api/planSubscriptionApi'
import LinkButton from '../../../components/link-button/link-button'
import PlanSubscriptionsList from '../../../components/plan-subscriptions/plan-subscriptions-list/plan-subscriptions-list'
import { getDateTime } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum } from '../../../share-types'
import { formatOrderHistoryStatus } from '../../order-histories-container/order-history-status'
import {
  getPlanSubscriptionAutoRenewStatusLabel,
  getPlanSubscriptionCyclePurchaseTypeLabel,
  getPlanSubscriptionCycleStatusLabel,
  getPlanSubscriptionStatusLabel,
} from '../constant'
import {
  IPlanSubscription,
  IPlanSubscriptionDetails,
  PlanSubscriptionAutoRenewStatus,
  PlanSubscriptionFilter,
  PlanSubscriptionRenewalMode,
  PlanSubscriptionStatus,
} from '../share-type'
import { Button, Card, Descriptions, Popconfirm, Table, notification, Row, Col } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const localPagination = {
  defaultPageSize: 10,
  showSizeChanger: true,
  hideOnSinglePage: true,
  pageSizeOptions: [10, 20, 50],
}

const defaultPageInfo: PageInfo = { currentPage: 1, pageSize: 10, count: 0 }

const PlanSubscription = () => {
  const { id } = useParams()
  const [details, setDetails] = useState<IPlanSubscriptionDetails>()
  const [userSubscriptions, setUserSubscriptions] = useState<IPlanSubscription[]>([])
  const [userSubscriptionsPageInfo, setUserSubscriptionsPageInfo] =
    useState<PageInfo>(defaultPageInfo)

  const loadUserSubscriptions = (userId: number, page = 0, size = defaultPageInfo.pageSize) => {
    const filter: PlanSubscriptionFilter = { userId, page, size }
    getPlanSubscriptions(filter).then(response => {
      setUserSubscriptions(response.data)
      setUserSubscriptionsPageInfo({
        ...response.pageInfo,
        currentPage: response.pageInfo.currentPage + 1,
      })
    })
  }

  const loadDetails = () => {
    if (id) {
      getPlanSubscription(Number(id)).then(response => {
        setDetails(response)
        loadUserSubscriptions(response.planSubscription.userId)
      })
    }
  }

  useEffect(loadDetails, [id])

  if (!details) {
    return null
  }

  const { planSubscription, cycles } = details
  const canCancel =
    planSubscription.status === PlanSubscriptionStatus.ACTIVE &&
    planSubscription.renewalMode === PlanSubscriptionRenewalMode.AUTO_RENEW &&
    planSubscription.autoRenewStatus === PlanSubscriptionAutoRenewStatus.ACTIVE
  const cycleColumns: ColumnsType<IPlanSubscriptionDetails['cycles'][number]> = [
    { title: 'ID', dataIndex: 'id' },
    {
      title: translate('planSubscriptions.order'),
      dataIndex: 'orderHistoryId',
      render: orderHistoryId => (
        <LinkButton to={`/order-histories/${orderHistoryId}`}>{orderHistoryId}</LinkButton>
      ),
    },
    {
      title: translate('planSubscriptions.status'),
      dataIndex: 'status',
      render: getPlanSubscriptionCycleStatusLabel,
    },
    {
      title: translate('planSubscriptions.purchaseType'),
      dataIndex: 'purchaseType',
      render: getPlanSubscriptionCyclePurchaseTypeLabel,
    },
    {
      title: translate('orderHistories.status'),
      dataIndex: 'orderStatus',
      render: value => (value ? formatOrderHistoryStatus(value) : '-'),
    },
    {
      title: translate('orderHistories.totalFee'),
      dataIndex: 'totalFee',
      render: value => (value == null ? '-' : `¥${value / 100}`),
    },
    {
      title: translate('planSubscriptions.validFrom'),
      dataIndex: 'validFrom',
      render: value => (value ? getDateTime(value) : '-'),
    },
    {
      title: translate('planSubscriptions.validTo'),
      dataIndex: 'validTo',
      render: value => (value ? getDateTime(value) : '-'),
    },
    {
      title: translate('planSubscriptions.creditBatch'),
      render: record =>
        record.creditBatch && (
          <LinkButton to={`/credit-batches/${record.creditBatch.id}`}>
            {`${record.creditBatch.remainingCredits}/${record.creditBatch.issuedCredits}`}
          </LinkButton>
        ),
    },
  ]

  const onCancel = () => {
    cancelPlanSubscriptionAutoRenewal(planSubscription.id).then(() => {
      notification.success({ title: translate('planSubscriptions.cancelSuccess') })
      loadDetails()
    })
  }

  const onUserSubscriptionsPaginationChange = (page: number, pageSize: number) => {
    loadUserSubscriptions(planSubscription.userId, page - 1, pageSize)
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card
          title={translate('planSubscriptions.detailTitle')}
          extra={
            canCancel && (
              <AuthPermission permission={PermissionsEnum.ORDER_HISTORIES_WRITE}>
                <Popconfirm
                  title={translate('planSubscriptions.cancelConfirm')}
                  onConfirm={onCancel}
                  okText={translate('yes')}
                  cancelText={translate('no')}
                >
                  <Button danger>{translate('planSubscriptions.cancelAutoRenewal')}</Button>
                </Popconfirm>
              </AuthPermission>
            )
          }
        >
          <Descriptions
            bordered
            column={1}
            size="small"
            styles={{ label: { fontWeight: 600, width: 200 } }}
          >
            <Descriptions.Item label="ID">{planSubscription.id}</Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.userId')}>
              {planSubscription.userId}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.planId')}>
              <LinkButton to={`/plans/${planSubscription.planId}`}>
                {planSubscription.planExternalId ?? planSubscription.planId}
              </LinkButton>
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.status')}>
              {getPlanSubscriptionStatusLabel(planSubscription.status)}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.renewalMode')}>
              {translate(`planSubscriptions.renewalMode.${planSubscription.renewalMode}`)}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.autoRenewStatus')}>
              {getPlanSubscriptionAutoRenewStatusLabel(planSubscription.autoRenewStatus)}
            </Descriptions.Item>
            {planSubscription.renewalMode === PlanSubscriptionRenewalMode.AUTO_RENEW && (
              <>
                <Descriptions.Item label={translate('planSubscriptions.wechatContractPlanId')}>
                  {planSubscription.wechatContractPlanId}
                </Descriptions.Item>
                <Descriptions.Item label={translate('planSubscriptions.contractId')}>
                  {planSubscription.contractId ?? '-'}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label={translate('planSubscriptions.currentPeriodStartedAt')}>
              {planSubscription.currentPeriodStartedAt
                ? getDateTime(planSubscription.currentPeriodStartedAt)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.currentPeriodEndsAt')}>
              {planSubscription.currentPeriodEndsAt
                ? getDateTime(planSubscription.currentPeriodEndsAt)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.nextBillingAt')}>
              {planSubscription.nextBillingAt ? getDateTime(planSubscription.nextBillingAt) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.signedAt')}>
              {planSubscription.signedAt ? getDateTime(planSubscription.signedAt) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translate('planSubscriptions.cancelledAt')}>
              {planSubscription.cancelledAt ? getDateTime(planSubscription.cancelledAt) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Card title={translate('planSubscriptions.cyclesTitle')}>
          <Table
            size="small"
            rowKey="id"
            columns={cycleColumns}
            dataSource={cycles}
            pagination={localPagination}
          />
        </Card>
      </Col>
      <Col xs={24}>
        <Card title={translate('planSubscriptions.userSubscriptionsTitle')}>
          <PlanSubscriptionsList
            subscriptions={userSubscriptions}
            pagination={{
              current: userSubscriptionsPageInfo.currentPage,
              pageSize: userSubscriptionsPageInfo.pageSize,
              total: userSubscriptionsPageInfo.count,
              showSizeChanger: true,
            }}
            onPaginationChange={onUserSubscriptionsPaginationChange}
          />
        </Card>
      </Col>
    </Row>
  )
}

export default PlanSubscription
